import * as React from "react";
import { Dropdown, IDropdownOption, SearchBox } from "@fluentui/react";
import {
    FILE_ALL_KEY,
    FILE_SEARCH_PLACEHOLDER,
    withAllFilesFirst
} from "../../helpers/FilenameSelectionHelper";

type Props = {
    fileOptions: string[];
    value: string[];
    onChange: (next: string[]) => void;
    disabled?: boolean;
    label?: string;
    enableSearch?: boolean;
    placeholder?: string;
    "data-testid"?: string;
};

export default function FilenameMultiSelectDropdown({
    fileOptions,
    value,
    onChange,
    disabled,
    label = "Filename",
    enableSearch = true,
    placeholder,
    ...rest
}: Props) {
    const [searchText, setSearchText] = React.useState("");

    const options = React.useMemo<IDropdownOption[]>(() =>
        withAllFilesFirst(fileOptions),
        [fileOptions]);

    const allFileKeys = React.useMemo(
        () => fileOptions.map(String),
        [fileOptions]
    );

    const selectedKeys = React.useMemo(() => {
        if (value.includes(FILE_ALL_KEY)) {
            return [FILE_ALL_KEY, ...allFileKeys];
        }

        return value;
    }, [value, allFileKeys]);

    const filteredOptions = React.useMemo(() => {
        if (!enableSearch || !searchText) {
            return options;
        }

        const text = searchText.toLowerCase();

        return options.filter(option =>
            String(option.text).toLowerCase().includes(text)
        );
    }, [options, enableSearch, searchText]);

    const handleChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (!option) {
            return;
        }

        if (option.key === FILE_ALL_KEY) {
            onChange(value.includes(FILE_ALL_KEY) ? [] : [FILE_ALL_KEY]);
            return;
        }

        const clickedKey = String(option.key);

        const current = value.includes(FILE_ALL_KEY)
            ? allFileKeys
            : value.map(String);

        const next = current.includes(clickedKey)
            ? current.filter(x => x !== clickedKey)
            : [...current, clickedKey];

        const isAllSelected = allFileKeys.length > 0 &&
            allFileKeys.every(key => next.includes(key));

        onChange(isAllSelected ? [FILE_ALL_KEY] : next);
    };

    return (
        <Dropdown
            {...rest}
            label={label}
            options={filteredOptions}
            selectedKeys={selectedKeys}
            disabled={!!disabled}
            multiSelect
            onChange={handleChange}
            calloutProps={{ calloutMaxHeight: 270 }}
            onRenderList={(props, defaultRender) => (
                <div>
                    {enableSearch && (
                        <div style={{ padding: 4 }}>
                            <SearchBox
                                placeholder={placeholder || FILE_SEARCH_PLACEHOLDER}
                                value={searchText}
                                onChange={(_, newValue) => setSearchText(newValue || "")}
                            />
                        </div>
                    )}
                    {defaultRender?.(props)}
                </div>
            )}
            onRenderTitle={(items) => {
                if (!items || items.length === 0) {
                    return null;
                }

                const display = items
                    .filter(item => String(item.key) !== String(FILE_ALL_KEY))
                    .map(item => item.text)
                    .join(", ");

                return <span>{display}</span>;
            }}
        />
    );
}